import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createRsaaAction } from "../../components/utilities/ReduxUtils";
import type {
    ChemicalDataRequest,
    ChemicalDataResponse,
    ChemicalSearchRequest,
    ChemicalSearchResponse,
    ReactionChemical,
} from "../../data/models/schemas";
import { IReactionChemicalType } from "../actions/library";
import type { ReactionAmount } from "../../data/models/schemas";
import {
    refreshLicenseInfoMeta,
    withRemainingUnitsUpdateMeta,
} from "../utils/meteringMeta";

// Modal-specific actions that won't interfere with main pocketbook state
export const MODAL_SEARCH_COMPOUNDS = "MODAL_SEARCH_COMPOUNDS";
export const MODAL_LOAD_COMPOUNDS = "MODAL_LOAD_COMPOUNDS";

type ISearchCompoundsRequest = ChemicalSearchRequest;
type ISearchCompoundsResponse = ChemicalSearchResponse;
type ILoadCompoundsRequest = ChemicalDataRequest;
type ILoadCompoundsResponse = ChemicalDataResponse;

export const modalSearchCompounds = createRsaaAction<
    ISearchCompoundsRequest,
    ISearchCompoundsResponse,
    ISearchCompoundsRequest
>({
    path: "chemicals/search",
    method: "POST",
    type: MODAL_SEARCH_COMPOUNDS,
    meta: () => refreshLicenseInfoMeta,
});

export const modalLoadCompounds = createRsaaAction<
    ILoadCompoundsRequest,
    ILoadCompoundsResponse,
    ILoadCompoundsResponse
>({
    path: "chemicals/data",
    method: "POST",
    type: MODAL_LOAD_COMPOUNDS,
    meta: (p) =>
        withRemainingUnitsUpdateMeta({
            clearCompounds: p.requestContext?.clearCompounds,
        }),
});

export interface INewReactionChemical extends ReactionChemical {
    ui_id: string; // Chemical's UUID (used for API calls and UI tracking)
    name: string;
    chemical_type: IReactionChemicalType;
    amount?: ReactionAmount;
}

export interface ILibraryUIState {
    isLibraryOpen: boolean;
    selectedTab:
        "chemicals" | "reactions" | "multistep" | "stages" | "formulations";
    searchQuery: string;
    viewMode: "grid" | "list";
    selectedChemicalId: string | null;
    selectedReactionId: string | null;
    selectedMultiStepReactionId: string | null;
    selectedFormulationId: string | null;

    // Modal states
    showCreateChemicalModal: boolean;
    showCreateReactionModal: boolean;
    showEditChemicalModal: boolean;
    showEditReactionModal: boolean;
    showDeleteChemicalConfirm: boolean;
    showDeleteReactionConfirm: boolean;
    showDeleteRouteConfirm: boolean;
    showViewChemicalModal: boolean;
    showViewReactionModal: boolean;
    showViewMultiStepReactionModal: boolean;

    // Form states
    newChemicalName: string;
    newChemicalDescription: string;
    newChemicalSmiles: string;

    newReactionName: string;
    newReactionDescription: string;
    newReactionType: string;
    newReactionChemicals: INewReactionChemical[];

    editReactionChemicals: INewReactionChemical[];

    editingChemicalId: string | null;
    editingReactionId: string | null;
    editingMultiStepReactionId: string | null;
    deletingChemicalId: string | null;
    deletingReactionId: string | null;
    deletingRouteId: string | null;
    viewingChemicalId: string | null;
    viewingReactionId: string | null;
    viewingMultiStepReactionId: string | null;
    viewingChemicalData: unknown | null;
    viewingChemicalLoading: boolean;
    viewingChemicalLoadingState: "idle" | "loading" | "success" | "error";
    viewingChemicalActiveTab: "basic-info" | "analysis";
    viewingChemicalCompounds: unknown[];
    viewingChemicalSearchResults: unknown[];
    showImportReactionModal: boolean;
    importReactionModalMode: "single" | "multi-step";
    showUploadReactionModal: boolean;
    uploadReactionModalMode: "single" | "multi-step";
    showCreateMultiStepRouteModal: boolean;
}

const initialState: ILibraryUIState = {
    isLibraryOpen: false,
    selectedTab: "chemicals",
    searchQuery: "",
    viewMode: "grid",
    selectedChemicalId: null,
    selectedReactionId: null,
    selectedMultiStepReactionId: null,
    selectedFormulationId: null,

    showCreateChemicalModal: false,
    showCreateReactionModal: false,
    showEditChemicalModal: false,
    showEditReactionModal: false,
    showDeleteChemicalConfirm: false,
    showDeleteReactionConfirm: false,
    showDeleteRouteConfirm: false,
    showViewChemicalModal: false,
    showViewReactionModal: false,
    showViewMultiStepReactionModal: false,

    newChemicalName: "",
    newChemicalDescription: "",
    newChemicalSmiles: "",

    newReactionName: "",
    newReactionDescription: "",
    newReactionType: "",
    newReactionChemicals: [],

    editReactionChemicals: [],

    editingChemicalId: null,
    editingReactionId: null,
    editingMultiStepReactionId: null,
    deletingChemicalId: null,
    deletingReactionId: null,
    deletingRouteId: null,
    viewingChemicalId: null,
    viewingReactionId: null,
    viewingMultiStepReactionId: null,
    viewingChemicalData: null,
    viewingChemicalLoading: false,
    viewingChemicalLoadingState: "idle",
    viewingChemicalActiveTab: "basic-info",
    viewingChemicalCompounds: [],
    viewingChemicalSearchResults: [],
    showImportReactionModal: false,
    importReactionModalMode: "single",
    showUploadReactionModal: false,
    uploadReactionModalMode: "single",
    showCreateMultiStepRouteModal: false,
};

const libraryUISlice = createSlice({
    name: "libraryUI",
    initialState,
    reducers: {
        // Library visibility
        openLibrary: (
            state,
            action: PayloadAction<
                | "chemicals"
                | "reactions"
                | "multistep"
                | "stages"
                | "formulations"
                | undefined
            >
        ) => {
            state.isLibraryOpen = true;
            if (action.payload) {
                state.selectedTab = action.payload;
            }
        },
        closeLibrary: (state) => {
            state.isLibraryOpen = false;
            state.showImportReactionModal = false;
            state.showUploadReactionModal = false;
            state.showCreateMultiStepRouteModal = false;
            state.showViewMultiStepReactionModal = false;
            state.viewingMultiStepReactionId = null;
            state.editingMultiStepReactionId = null;
        },
        toggleLibrary: (state) => {
            state.isLibraryOpen = !state.isLibraryOpen;
        },
        openLibraryImportModal: (
            state,
            action: PayloadAction<"single" | "multi-step">
        ) => {
            state.showImportReactionModal = true;
            state.importReactionModalMode = action.payload;
            state.showUploadReactionModal = false;
            state.uploadReactionModalMode = action.payload;
            state.showCreateMultiStepRouteModal = false;
        },
        closeLibraryImportModal: (state) => {
            state.showImportReactionModal = false;
        },
        openLibraryUploadModal: (
            state,
            action: PayloadAction<"single" | "multi-step">
        ) => {
            state.showUploadReactionModal = true;
            state.uploadReactionModalMode = action.payload;
            state.showImportReactionModal = false;
            state.importReactionModalMode = action.payload;
            state.showCreateMultiStepRouteModal = false;
        },
        closeLibraryUploadModal: (state) => {
            state.showUploadReactionModal = false;
        },
        openCreateMultiStepRouteModal: (state) => {
            state.showCreateMultiStepRouteModal = true;
            state.showImportReactionModal = false;
            state.showUploadReactionModal = false;
            state.editingMultiStepReactionId = null;
        },
        closeCreateMultiStepRouteModal: (state) => {
            state.showCreateMultiStepRouteModal = false;
            state.editingMultiStepReactionId = null;
        },
        openEditMultiStepRouteModal: (state, action: PayloadAction<string>) => {
            state.showCreateMultiStepRouteModal = true;
            state.showImportReactionModal = false;
            state.showUploadReactionModal = false;
            state.editingMultiStepReactionId = action.payload;
        },
        openViewMultiStepReactionModal: (
            state,
            action: PayloadAction<string>
        ) => {
            state.showViewMultiStepReactionModal = true;
            state.viewingMultiStepReactionId = action.payload;
        },
        closeViewMultiStepReactionModal: (state) => {
            state.showViewMultiStepReactionModal = false;
            state.viewingMultiStepReactionId = null;
        },

        // Tab management
        setSelectedTab: (
            state,
            action: PayloadAction<
                | "chemicals"
                | "reactions"
                | "multistep"
                | "stages"
                | "formulations"
            >
        ) => {
            state.selectedTab = action.payload;
        },

        // Search and filters
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
        },
        setViewMode: (state, action: PayloadAction<"grid" | "list">) => {
            state.viewMode = action.payload;
        },

        // Selection
        setSelectedChemical: (state, action: PayloadAction<string | null>) => {
            state.selectedChemicalId = action.payload;
        },
        setSelectedReaction: (state, action: PayloadAction<string | null>) => {
            state.selectedReactionId = action.payload;
        },

        // Chemical modals
        openCreateChemicalModal: (state) => {
            state.showCreateChemicalModal = true;
            state.newChemicalName = "";
            state.newChemicalDescription = "";
            state.newChemicalSmiles = "";
        },
        closeCreateChemicalModal: (state) => {
            state.showCreateChemicalModal = false;
            state.newChemicalName = "";
            state.newChemicalDescription = "";
            state.newChemicalSmiles = "";
        },
        openEditChemicalModal: (state, action: PayloadAction<string>) => {
            state.showEditChemicalModal = true;
            state.editingChemicalId = action.payload;
        },
        closeEditChemicalModal: (state) => {
            state.showEditChemicalModal = false;
            state.editingChemicalId = null;
        },
        openDeleteChemicalConfirm: (state, action: PayloadAction<string>) => {
            state.showDeleteChemicalConfirm = true;
            state.deletingChemicalId = action.payload;
        },
        closeDeleteChemicalConfirm: (state) => {
            state.showDeleteChemicalConfirm = false;
            state.deletingChemicalId = null;
        },

        // Reaction modals
        openCreateReactionModal: (state) => {
            state.showCreateReactionModal = true;
            state.newReactionName = "";
            state.newReactionDescription = "";
            state.newReactionType = "";
            state.newReactionChemicals = [];
        },
        closeCreateReactionModal: (state) => {
            state.showCreateReactionModal = false;
            state.newReactionName = "";
            state.newReactionDescription = "";
            state.newReactionType = "";
            state.newReactionChemicals = [];
        },
        openEditReactionModal: (state, action: PayloadAction<string>) => {
            state.showEditReactionModal = true;
            state.editingReactionId = action.payload;
            state.editReactionChemicals = [];
        },
        closeEditReactionModal: (state) => {
            state.showEditReactionModal = false;
            state.editingReactionId = null;
            state.editReactionChemicals = [];
        },
        openDeleteReactionConfirm: (state, action: PayloadAction<string>) => {
            state.showDeleteReactionConfirm = true;
            state.deletingReactionId = action.payload;
        },
        closeDeleteReactionConfirm: (state) => {
            state.showDeleteReactionConfirm = false;
            state.deletingReactionId = null;
        },
        openDeleteRouteConfirm: (state, action: PayloadAction<string>) => {
            state.showDeleteRouteConfirm = true;
            state.deletingRouteId = action.payload;
        },
        closeDeleteRouteConfirm: (state) => {
            state.showDeleteRouteConfirm = false;
            state.deletingRouteId = null;
        },

        // View modals
        openViewChemicalModal: (state, action: PayloadAction<string>) => {
            state.showViewChemicalModal = true;
            state.viewingChemicalId = action.payload;
            state.viewingChemicalLoadingState = "idle";
            state.viewingChemicalActiveTab = "basic-info";
        },
        closeViewChemicalModal: (state) => {
            state.showViewChemicalModal = false;
            state.viewingChemicalId = null;
            state.viewingChemicalData = null;
            state.viewingChemicalLoading = false;
            state.viewingChemicalLoadingState = "idle";
            state.viewingChemicalActiveTab = "basic-info";
            state.viewingChemicalCompounds = [];
            state.viewingChemicalSearchResults = [];
        },
        openViewReactionModal: (state, action: PayloadAction<string>) => {
            state.showViewReactionModal = true;
            state.viewingReactionId = action.payload;
        },
        closeViewReactionModal: (state) => {
            state.showViewReactionModal = false;
            state.viewingReactionId = null;
        },

        // Chemical data loading for viewing
        setViewingChemicalLoading: (state, action: PayloadAction<boolean>) => {
            state.viewingChemicalLoading = action.payload;
        },
        setViewingChemicalData: (state, action: PayloadAction<unknown>) => {
            state.viewingChemicalData = action.payload;
            state.viewingChemicalLoading = false;
        },
        startViewingChemicalSearch: (state) => {
            state.viewingChemicalLoadingState = "loading";
        },
        setViewingChemicalSuccess: (state) => {
            state.viewingChemicalLoadingState = "success";
        },
        setViewingChemicalError: (state) => {
            state.viewingChemicalLoadingState = "error";
        },
        requestViewingChemicalAnalysis: (state) => {
            state.viewingChemicalLoadingState = "loading";
        },
        setViewingChemicalActiveTab: (
            state,
            action: PayloadAction<"basic-info" | "analysis">
        ) => {
            state.viewingChemicalActiveTab = action.payload;
        },

        // Form setters - Chemical
        setNewChemicalName: (state, action: PayloadAction<string>) => {
            state.newChemicalName = action.payload;
        },
        setNewChemicalDescription: (state, action: PayloadAction<string>) => {
            state.newChemicalDescription = action.payload;
        },
        setNewChemicalSmiles: (state, action: PayloadAction<string>) => {
            state.newChemicalSmiles = action.payload;
        },

        // Form setters - Reaction
        setNewReactionName: (state, action: PayloadAction<string>) => {
            state.newReactionName = action.payload;
        },
        setNewReactionDescription: (state, action: PayloadAction<string>) => {
            state.newReactionDescription = action.payload;
        },
        setNewReactionType: (state, action: PayloadAction<string>) => {
            state.newReactionType = action.payload;
        },

        // Reaction chemicals management
        addChemicalToNewReaction: (
            state,
            action: PayloadAction<INewReactionChemical>
        ) => {
            state.newReactionChemicals.push(action.payload);
        },
        removeChemicalFromNewReaction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.newReactionChemicals = state.newReactionChemicals.filter(
                (chem) => chem.ui_id !== action.payload
            );
        },
        updateReactionChemical: (
            state,
            action: PayloadAction<INewReactionChemical>
        ) => {
            const index = state.newReactionChemicals.findIndex(
                (chem) => chem.ui_id === action.payload.ui_id
            );
            if (index !== -1) {
                state.newReactionChemicals[index] = action.payload;
            }
        },

        // Edit reaction chemicals management
        addChemicalToEditReaction: (
            state,
            action: PayloadAction<INewReactionChemical>
        ) => {
            state.editReactionChemicals.push(action.payload);
        },
        removeChemicalFromEditReaction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.editReactionChemicals = state.editReactionChemicals.filter(
                (chem) => chem.ui_id !== action.payload
            );
        },
        updateEditReactionChemical: (
            state,
            action: PayloadAction<INewReactionChemical>
        ) => {
            const index = state.editReactionChemicals.findIndex(
                (chem) => chem.ui_id === action.payload.ui_id
            );
            if (index !== -1) {
                state.editReactionChemicals[index] = action.payload;
            }
        },
        setEditReactionChemicals: (
            state,
            action: PayloadAction<INewReactionChemical[]>
        ) => {
            state.editReactionChemicals = action.payload;
        },

        // Clear all state
        resetLibraryUI: () => initialState,

        // Reset load attempts (for when library reopens)
        resetLoadAttempts: () => {},
    },
    extraReducers: (builder) => {
        builder
            // Listen to modal-specific search compounds
            .addCase(modalSearchCompounds.request, (state) => {
                state.viewingChemicalLoadingState = "loading";
            })
            .addCase(
                modalSearchCompounds.success,
                (state, action: PayloadAction<ISearchCompoundsResponse>) => {
                    const models = action.payload.models || [];
                    // Store search results and keep loading until we get full compound data
                    state.viewingChemicalLoadingState = "loading";
                    // Handle both single and multiple results
                    if (models.length === 1) {
                        state.viewingChemicalCompounds = models;
                    } else {
                        state.viewingChemicalSearchResults = models;
                    }
                }
            )
            .addCase(modalSearchCompounds.error, (state) => {
                state.viewingChemicalLoadingState = "error";
            })
            // Listen to modal-specific load compounds
            .addCase(modalLoadCompounds.request, (state) => {
                state.viewingChemicalLoadingState = "loading";
            })
            .addCase(
                modalLoadCompounds.success,
                (state, action: PayloadAction<ILoadCompoundsResponse>) => {
                    state.viewingChemicalLoadingState = "success";
                    state.viewingChemicalCompounds =
                        action.payload.models || [];
                }
            )
            .addCase(modalLoadCompounds.error, (state) => {
                state.viewingChemicalLoadingState = "error";
            });
    },
});

export const {
    openLibrary,
    closeLibrary,
    toggleLibrary,
    setSelectedTab,
    setSearchQuery,
    setViewMode,
    setSelectedChemical,
    setSelectedReaction,
    openCreateChemicalModal,
    closeCreateChemicalModal,
    openEditChemicalModal,
    closeEditChemicalModal,
    openDeleteChemicalConfirm,
    closeDeleteChemicalConfirm,
    openCreateReactionModal,
    closeCreateReactionModal,
    openEditReactionModal,
    closeEditReactionModal,
    openDeleteReactionConfirm,
    closeDeleteReactionConfirm,
    openDeleteRouteConfirm,
    closeDeleteRouteConfirm,
    openViewChemicalModal,
    closeViewChemicalModal,
    openViewReactionModal,
    closeViewReactionModal,
    setViewingChemicalLoading,
    setViewingChemicalData,
    startViewingChemicalSearch,
    setViewingChemicalSuccess,
    setViewingChemicalError,
    requestViewingChemicalAnalysis,
    setViewingChemicalActiveTab,
    setNewChemicalName,
    setNewChemicalDescription,
    setNewChemicalSmiles,
    setNewReactionName,
    setNewReactionDescription,
    setNewReactionType,
    addChemicalToNewReaction,
    removeChemicalFromNewReaction,
    updateReactionChemical,
    addChemicalToEditReaction,
    removeChemicalFromEditReaction,
    updateEditReactionChemical,
    setEditReactionChemicals,
    resetLibraryUI,
    resetLoadAttempts,
    openLibraryImportModal,
    closeLibraryImportModal,
    openLibraryUploadModal,
    closeLibraryUploadModal,
    openCreateMultiStepRouteModal,
    closeCreateMultiStepRouteModal,
    openEditMultiStepRouteModal,
    openViewMultiStepReactionModal,
    closeViewMultiStepReactionModal,
} = libraryUISlice.actions;

export default libraryUISlice.reducer;
